/**
 * t302u: attendance registers. A checklist template of kind 'register' has no items: each run lists the people in
 * scope (building, optional departments and positions) whose shift overlaps the run's window, plus anyone added by
 * hand. The supervisor marks each person Present, Late (optional arrival time), Absent or On leave, with an optional
 * note. A later run the same day carries earlier marks forward (shown muted, still changeable). Pure rules shared by
 * the API, the desktop page and the phone screen.
 */
import { DateTime } from 'luxon';

export type ChecklistKind = 'checklist' | 'register';
export type RegisterStatus = 'present' | 'late' | 'absent' | 'on_leave';
export const REGISTER_STATUSES: readonly RegisterStatus[] = ['present', 'late', 'absent', 'on_leave'];
export const REGISTER_STATUS_LABEL: Record<RegisterStatus, string> = { present: 'Present', late: 'Late', absent: 'Absent', on_leave: 'On leave' };
export const isRegisterStatus = (s: unknown): s is RegisterStatus => typeof s === 'string' && (REGISTER_STATUSES as readonly string[]).includes(s);

/** A mark stored on this run. */
export interface RegisterMark {
  status: RegisterStatus | null; arrived_at: string | null; note: string | null;
  marked_by: string | null; marked_by_name: string | null; marked_at: string | null;
  source: 'marked' | 'carried' | 'leave' | 'added';
  /** Carried marks (stored when the run was completed): the time of the run they came from, e.g. "09:00". */
  carried_run_time?: string | null;
}
/** The latest mark from an earlier run the same day (same register, same building). */
export interface RegisterCarried {
  run_id: string; run_time: string | null; status: RegisterStatus; arrived_at: string | null; note: string | null;
  marked_by: string | null; marked_by_name: string | null; marked_at: string;
}
export interface RegisterPerson {
  user_id: string; display_name: string;
  position_id: string | null; position_name: string | null;
  /** The shift that puts them on this run (null when added by hand and not rostered). */
  shift_id: string | null; shift_starts_at: string | null; shift_ends_at: string | null;
  rostered: boolean; added_by_hand: boolean;
  /** Approved leave covering the run's date. */
  on_leave: boolean; leave_type: string | null;
  /** First clock-in at this building that overlaps the run (a hint only; never marks anyone). */
  clock_in_at: string | null;
  mark: RegisterMark | null;
  carried: RegisterCarried | null;
}
export interface RegisterView {
  location_id: string; location_name: string; timezone: string;
  team_ids: string[]; team_names: string[]; position_ids: string[]; position_names: string[];
  absent_follow_up: boolean;
  people: RegisterPerson[];
}
export interface RegisterCounts { present: number; late: number; absent: number; on_leave: number; unmarked: number; total: number }

/** What a person's row shows: their mark on this run, else the carried one, else On leave from approved leave. */
export interface EffectiveMark {
  status: RegisterStatus; arrived_at: string | null; note: string | null; by: string | null; at: string | null;
  origin: 'own' | 'carried' | 'leave'; run_time?: string | null;
}
export function effectiveMark(p: Pick<RegisterPerson, 'mark' | 'carried' | 'on_leave'>): EffectiveMark | null {
  if (p.mark?.status) return { status: p.mark.status, arrived_at: p.mark.arrived_at, note: p.mark.note, by: p.mark.marked_by_name, at: p.mark.marked_at, origin: p.mark.source === 'carried' ? 'carried' : p.mark.source === 'leave' ? 'leave' : 'own', run_time: p.mark.carried_run_time ?? null };
  if (p.carried) return { status: p.carried.status, arrived_at: p.carried.arrived_at, note: p.carried.note, by: p.carried.marked_by_name, at: p.carried.marked_at, origin: 'carried', run_time: p.carried.run_time };
  if (p.on_leave) return { status: 'on_leave', arrived_at: null, note: null, by: null, at: null, origin: 'leave' };
  return null;
}
/** Still to ask about on this run (no mark of any kind). */
export const needsMark = (p: Pick<RegisterPerson, 'mark' | 'carried' | 'on_leave'>) => effectiveMark(p) === null;

export function registerCounts(people: readonly Pick<RegisterPerson, 'mark' | 'carried' | 'on_leave'>[]): RegisterCounts {
  const c: RegisterCounts = { present: 0, late: 0, absent: 0, on_leave: 0, unmarked: 0, total: people.length };
  for (const p of people) { const m = effectiveMark(p); if (m) c[m.status]++; else c.unmarked++; }
  return c;
}
/** "18 present · 2 late · 1 absent" (zeros left out, present always shown; "· 1 on leave" when any; "· 3 to mark" while open). */
export function formatRegisterCounts(c: Partial<RegisterCounts> | null | undefined, opts: { unmarked?: boolean } = {}): string {
  if (!c) return '';
  const parts = [`${c.present ?? 0} present`];
  if (c.late) parts.push(`${c.late} late`);
  if (c.absent) parts.push(`${c.absent} absent`);
  if (c.on_leave) parts.push(`${c.on_leave} on leave`);
  if (opts.unmarked && c.unmarked) parts.push(`${c.unmarked} to mark`);
  return parts.join(' · ');
}

/** Groups by position name (A-Z, "No position" last); inside, by shift start then name. */
export function registerGroups<P extends Pick<RegisterPerson, 'position_id' | 'position_name' | 'shift_starts_at' | 'display_name'>>(people: readonly P[]): { key: string; label: string; people: P[] }[] {
  const by = new Map<string, { label: string; people: P[] }>();
  for (const p of people) {
    const k = p.position_id ?? '';
    const g = by.get(k) ?? { label: p.position_name ?? 'No position', people: [] };
    g.people.push(p); by.set(k, g);
  }
  return [...by.entries()].map(([key, g]) => ({
    key, label: g.label,
    people: g.people.sort((a, b) => (a.shift_starts_at ?? '9').localeCompare(b.shift_starts_at ?? '9') || a.display_name.localeCompare(b.display_name, 'en-GB')),
  })).sort((a, b) => Number(a.key === '') - Number(b.key === '') || a.label.localeCompare(b.label, 'en-GB'));
}

const hhmm = (iso: string | null | undefined, zone: string) => (iso ? DateTime.fromISO(iso, { zone: 'utc' }).setZone(zone).toFormat('HH:mm') : '');
/** "09:00–17:00" in the building's zone. */
export const shiftLabel = (p: Pick<RegisterPerson, 'shift_starts_at' | 'shift_ends_at'>, zone = 'Europe/London') =>
  p.shift_starts_at && p.shift_ends_at ? `${hhmm(p.shift_starts_at, zone)}–${hhmm(p.shift_ends_at, zone)}` : '';
/** "Clocked in 08:52" (or ''). */
export const clockInHint = (p: Pick<RegisterPerson, 'clock_in_at'>, zone = 'Europe/London') => (p.clock_in_at ? `Clocked in ${hhmm(p.clock_in_at, zone)}` : '');

/** "Present at 09:00 · Sarah", "Late 09:12 at 09:00 · Sarah", "Absent · Tom", "On leave" (first names only). */
export function describeMark(m: EffectiveMark | null, zone = 'Europe/London'): string {
  if (!m) return '';
  if (m.origin === 'leave') return 'On leave';
  const label = REGISTER_STATUS_LABEL[m.status] + (m.status === 'late' && m.arrived_at ? ` ${hhmm(m.arrived_at, zone)}` : '');
  const at = m.origin === 'carried' && m.run_time ? ` at ${m.run_time}` : '';
  const who = m.by ? ` · ${m.by.split(' ')[0]}` : '';
  return `${label}${at}${who}`;
}

/** Offline taps waiting to be sent, laid over the run as the server last sent it (newest tap wins). */
export interface PendingMark { user_id: string; status: RegisterStatus; arrived_at?: string | null; note?: string | null; marked_at: string }
export function applyPendingMarks<P extends RegisterPerson>(people: readonly P[], pending: readonly PendingMark[], me: { id: string; name: string | null }): P[] {
  if (!pending.length) return [...people];
  const latest = new Map<string, PendingMark>();
  for (const m of [...pending].sort((a, b) => a.marked_at.localeCompare(b.marked_at))) latest.set(m.user_id, m);
  return people.map(p => {
    const m = latest.get(p.user_id);
    if (!m) return p;
    return { ...p, mark: { status: m.status, arrived_at: m.status === 'late' ? m.arrived_at ?? null : null, note: m.note !== undefined ? m.note ?? null : p.mark?.note ?? null,
      marked_by: me.id, marked_by_name: me.name, marked_at: m.marked_at, source: 'marked' } };
  });
}

/** People to mark Present with "Mark everyone remaining present": no mark of any kind yet. */
export const remainingUserIds = (people: readonly Pick<RegisterPerson, 'user_id' | 'mark' | 'carried' | 'on_leave'>[]) => people.filter(needsMark).map(p => p.user_id);

/** One person's line in the attendance report (days with each outcome in the range). */
export interface RegisterReportRow {
  user_id: string; display_name: string; position_name: string | null;
  days: number; present: number; late: number; absent: number; on_leave: number; marks: number;
}
/**
 * A person's outcome for one day from that day's marks (oldest run first): the last run's mark, except that a day with
 * any Late mark and a final Present counts as Late (they arrived late, then were carried as present).
 */
export function dayOutcome(statuses: readonly RegisterStatus[]): RegisterStatus | null {
  if (!statuses.length) return null;
  const last = statuses[statuses.length - 1];
  return last === 'present' && statuses.includes('late') ? 'late' : last;
}
